/**
 * Root-level calculation + layout constants for the Consecutive Master Schedule view.
 *
 * This view lays out an N-on / M-off consecutive-cycle department as a per-day grid rather than the
 * weekday grid used by MasterScheduleV2. The cycle (working + rest days) is chosen at runtime via
 * the "Consecutive period" control, so the period-dependent layout/math is derived from a
 * `daysPerBlock`/`cycleLength` value here rather than hardcoded.
 */

/** The user-chosen rotation: N working days followed by M rest days (e.g. 4/2). */
export type ConsecutivePeriod = { workingDays: number; restDays: number };
/** Default rotation (4-on / 2-off). */
export const DEFAULT_CONSECUTIVE_PERIOD: ConsecutivePeriod = { workingDays: 4, restDays: 2 };
/** Selectable range for each of the two period inputs. */
export const CONSECUTIVE_PERIOD_MIN = 1;
export const CONSECUTIVE_PERIOD_MAX = 14;

/** Cycle length (days per block) for a period — the modulo base for column labeling / cycle-start math. */
export const getCycleLength = (period: ConsecutivePeriod): number => period.workingDays + period.restDays;

/** How many consecutive cycle blocks are shown side by side ("Week 1" + "Week 2"). */
export const CYCLE_BLOCKS_SHOWN = 2;
/** Leading (non-day) columns: Location + Shift. */
export const LEADING_COLUMNS = 2;

/** Root element id — used for nurse-cell highlight class toggling (mirrors MasterScheduleV2). */
export const CONSECUTIVE_MASTER_SCHEDULE_ROOT_ID = "consecutive-master-schedule-root";

/** Layout dimensions for the CSS grid columns. */
export const LOCATION_COLUMN_WIDTH = "160px";
export const SHIFT_COLUMN_WIDTH = "120px";
export const DAY_COLUMN_MIN_WIDTH = "64px";
export const BLOCK_DIVIDER_WIDTH = "12px";

/**
 * Shared CSS grid column template used by BOTH the sticky header and every virtualized row, so the
 * columns line up. Layout: Location | Shift | `daysPerBlock` day cols (block 1) | divider | same (block 2).
 */
export const getGridTemplateColumns = (daysPerBlock: number): string =>
	`${LOCATION_COLUMN_WIDTH} ${SHIFT_COLUMN_WIDTH} repeat(${daysPerBlock}, minmax(${DAY_COLUMN_MIN_WIDTH}, 1fr)) ${BLOCK_DIVIDER_WIDTH} repeat(${daysPerBlock}, minmax(${DAY_COLUMN_MIN_WIDTH}, 1fr))`;

/** Minimum grid width before horizontal scroll kicks in — scales with the column count. */
export const getGridMinWidth = (daysPerBlock: number): string => {
	const px = (v: string) => Number.parseInt(v, 10);
	const leading = px(LOCATION_COLUMN_WIDTH) + px(SHIFT_COLUMN_WIDTH) + px(BLOCK_DIVIDER_WIDTH);
	const dayCols = CYCLE_BLOCKS_SHOWN * daysPerBlock * px(DAY_COLUMN_MIN_WIDTH);
	return `${Math.max(leading + dayCols, 1000)}px`;
};

/**
 * Row-height constants for react-window virtualization. Heights are computed deterministically (no
 * DOM measurement), so the rendered cell MUST honor these exact per-item sizes — the cell enforces
 * `CELL_ITEM_HEIGHT` on every shift chip AND the "+" button (which is otherwise a 40px MUI button).
 * If these get out of sync the tallest cell's last item clips.
 */
export const SECTION_HEADER_HEIGHT = 34;
/** Fixed height of one stacked item inside a cell — a shift chip or the "+" add button. */
export const CELL_ITEM_HEIGHT = 28;
/** Vertical gap between stacked items in a cell. */
export const CELL_ITEM_GAP = 2;
/** Total vertical padding inside a cell (split top/bottom). */
export const CELL_PADDING_Y = 6;
/** Bottom divider drawn between rows (consumed from the row slot under border-box). */
export const ROW_BORDER_HEIGHT = 1;
/** Floor so a single-item row never looks cramped. */
export const MIN_ROW_HEIGHT = 40;
/** Rows kept mounted above/below the viewport (kept low; higher hurts scroll perf). */
export const ROW_OVERSCAN_COUNT = 3;
